import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Search } from 'lucide-react';
import MemberCard from './GroupMemberCard';

const MemberList = ({
  members = [],
  currentUser,                 // optional (for action gating)
  isOwner = false,             // from page-level perms
  isAdmin = false,             // from page-level perms
  canMute = false,             // owner/admin/moderator -- from page-level perms
  viewerRole,                   // the current viewer's own role string, for rank comparison
  isLoading = false,
  onAddMember,                 // () => void  (open invite flow)
  onRemoveMember,              // (userId) => void
  onPromoteMember,              // (userId) => void
  onDemoteMember,               // (userId) => void
  onTransferOwnership,          // (userId) => void
  onMuteMember,                 // (userId) => void
  onUnmuteMember,                // (userId) => void
}) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'admins' | 'recent'

  const filtered = useMemo(() => {
    let list = members;

    if (q.trim()) {
      const term = q.toLowerCase();
      list = list.filter((m) => {
        const name = (m?.username || m?.name || '').toLowerCase();
        const idStr = String(m?.user_id ?? m?.id ?? '');
        return name.includes(term) || idStr.includes(term);
      });
    }

    if (roleFilter === 'admins') {
      list = list.filter((m) => m?.role === 'admin' || m?.role === 'owner');
    } else if (roleFilter === 'recent') {
      list = [...list].sort((a, b) => {
        const ta = new Date(a?.joined_at || 0).getTime();
        const tb = new Date(b?.joined_at || 0).getTime();
        return tb - ta;
      });
    }

    return list;
  }, [members, q, roleFilter]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-gray-600 dark:text-gray-400 mt-3">{t('groups.memberList.loadingMembers')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
          <Users size={20} className="text-blue-400" />
          <span>{t('groups.memberList.groupMembers')}</span>
          <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full text-sm">
            {members.length}
          </span>
        </h3>

        {isAdmin && (
          <button
            onClick={() => onAddMember?.()}
            className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white rounded-xl flex items-center space-x-2 transition-all duration-300"
          >
            <UserPlus size={16} />
            <span>{t('groups.memberList.inviteMembers')}</span>
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="bg-cream-100 dark:bg-gray-700/30 rounded-2xl p-6 border border-cream-300 dark:border-gray-600/30">
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400" size={18} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('groups.memberList.searchPlaceholder')}
              className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('groups.memberList.allMembers')}</option>
            <option value="admins">{t('groups.memberList.ownersAndAdmins')}</option>
            <option value="recent">{t('groups.memberList.recentJoins')}</option>
          </select>
        </div>

        {/* Grid */}
        <div className="grid gap-4">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={48} />
              <p className="text-gray-600 dark:text-gray-400 text-lg">{t('groups.memberList.noMembers')}</p>
              <p className="text-gray-500 dark:text-gray-500 text-sm">{t('groups.memberList.tryDifferentSearch')}</p>
            </div>
          ) : (
            filtered.map((member, idx) => {
              const uid = member?.user_id ?? member?.id ?? idx;
              const role = member?.role;
              const isMemberOwner = role === 'owner';
              const isMemberAdmin = role === 'admin';
              const isSelf = currentUser && uid === (currentUser.id ?? currentUser.user_id);

              return (
                <MemberCard
                  key={uid}
                  member={member}
                  isOwner={isMemberOwner}
                  isAdmin={isMemberAdmin}
                  isSelf={isSelf}
                  canManage={isOwner || isAdmin}
                  canMute={canMute}
                  viewerRole={viewerRole}
                  viewerIsOwner={isOwner}
                  onRemove={onRemoveMember}
                  onPromote={onPromoteMember}
                  onDemote={onDemoteMember}
                  onTransferOwnership={onTransferOwnership}
                  onMute={onMuteMember}
                  onUnmute={onUnmuteMember}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberList;
